// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Git Integration Page
// Route: /git
// apps/web/src/routes/git.tsx
//
// Connect GitHub repos, sync commits, map to work items. Shows commit
// history with diff stats, branch info, and auto-linked work items.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  EngineTooltip,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import {
  useGitRepositories, useGitCommits, useGitMutations,
  useGitHubOAuthStatus, useGitHubOAuth, useGitHubOAuthRepos,
} from '../lib/api/git';
import { useProjects } from '../lib/api/projects';
import type { GitRepository, GitCommit, SyncResult } from '../lib/api/git';

export const Route = createFileRoute('/git')({
  component: GitIntegrationPage,
});

const SYNC_STATUS_COLOR: Record<string, string> = {
  Pending: C.dim,
  Syncing: C.amber,
  Synced: C.neon,
  Failed: C.coral,
};

function GitIntegrationPage() {
  const { data: repos, loading, refetch } = useGitRepositories();
  const { data: projects } = useProjects();
  const { data: oauthStatus, refetch: refetchOAuth } = useGitHubOAuthStatus();
  const oauth = useGitHubOAuth();
  const { data: oauthRepos } = useGitHubOAuthRepos(oauthStatus?.connected ?? false);
  const mutations = useGitMutations();
  const [showConnect, setShowConnect] = useState(false);
  const [selectedRepoId, setSelectedRepoId] = useState<number>(0);
  const [lastSync, setLastSync] = useState<SyncResult | null>(null);

  const { data: commits, loading: commitsLoading, refetch: refetchCommits } = useGitCommits(selectedRepoId);

  // Check if we just came back from GitHub OAuth
  const searchParams = new URLSearchParams(window.location.search);
  const justConnected = searchParams.get('github') === 'connected';
  useState(() => {
    if (justConnected) {
      refetchOAuth();
      refetch();
      window.history.replaceState({}, '', '/git');
    }
  });

  const metrics = useMemo(() => {
    if (!repos) return null;
    const totalCommits = repos.reduce((s, r) => s + r.totalCommits, 0);
    const synced = repos.filter(r => r.syncStatus === 'Synced').length;
    return { total: repos.length, synced, totalCommits };
  }, [repos]);

  const countCommits = useCounter(metrics?.totalCommits ?? 0, 700, 300);

  return (
    <EnginePageShell
      title="Git Integration"
      subtitle="SOURCE CONTROL"
      icon="⟁"
      accentColor={C.cyan}
      actions={
        <button onClick={() => setShowConnect(true)} style={{
          background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
          fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '6px 16px',
          borderRadius: 6, cursor: 'pointer', letterSpacing: '0.08em',
        }}>
          + CONNECT REPO
        </button>
      }
    >
      {/* ── Metrics ───────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        <MetricCard label="Repositories" value={metrics?.total ?? 0} icon="⟁" color={C.cyan}
          subtitle={`${metrics?.synced ?? 0} synced`}
          tooltip={<div>Connected GitHub repositories across all projects.</div>} />
        <MetricCard label="Total Commits" value={countCommits} icon="📝" color={C.neon}
          tooltip={<div>Commits synced from all connected repositories.</div>} />
      </div>

      {/* ── GitHub Connection ─────────────────────────────────────── */}
      <SectionLabel color={C.violet}>GitHub Connection</SectionLabel>
      <GlassPanel accent={C.violet} delay={0.2} style={{ padding: 16, marginBottom: 20 }}>
        {oauthStatus?.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {oauthStatus.avatarUrl && (
              <img src={oauthStatus.avatarUrl} alt="" style={{ width: 36, height: 36, borderRadius: '50%', border: `2px solid ${C.neon}40` }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{oauthStatus.login}</span>
                <StatusBadge label="CONNECTED" color={C.neon} />
              </div>
              <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginTop: 3 }}>
                Connected {oauthStatus.connectedAt ? new Date(oauthStatus.connectedAt).toLocaleDateString() : ''}
                {oauthRepos ? ` · ${oauthRepos.length} repos accessible` : ''}
              </div>
            </div>
            <button
              onClick={async () => { await oauth.disconnect(); refetchOAuth(); }}
              style={{
                background: `${C.coral}12`, border: `1px solid ${C.coral}30`, color: C.coral,
                fontFamily: F.mono, fontSize: 9, padding: '4px 12px', borderRadius: 4, cursor: 'pointer',
              }}
            >DISCONNECT</button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>&#x1f517;</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Connect GitHub</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 16 }}>
              Authorize ForgeTrack to access your repositories for commit syncing and work item linking.
            </div>
            <button
              onClick={oauth.authorize}
              disabled={oauth.loading}
              style={{
                background: `${C.violet}20`, border: `1px solid ${C.violet}50`, color: C.violet,
                fontFamily: F.mono, fontSize: 12, fontWeight: 600, padding: '10px 28px',
                borderRadius: 8, cursor: 'pointer', letterSpacing: '0.06em',
                opacity: oauth.loading ? 0.5 : 1,
              }}
            >
              {oauth.loading ? 'REDIRECTING...' : 'AUTHORIZE WITH GITHUB'}
            </button>
            {oauth.error && (
              <div style={{ fontSize: 11, fontFamily: F.mono, color: C.coral, marginTop: 10 }}>{oauth.error}</div>
            )}
          </div>
        )}
      </GlassPanel>

      {/* ── Connected Repos ───────────────────────────────────────────── */}
      <SectionLabel color={C.cyan}>Connected Repositories</SectionLabel>
      {loading ? (
        <GlassPanel delay={0.3}><LoadingShimmer height={100} /></GlassPanel>
      ) : repos && repos.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
          {repos.map((repo, i) => (
            <RepoCard
              key={repo.id}
              repo={repo}
              index={i}
              selected={selectedRepoId === repo.id}
              onSelect={() => setSelectedRepoId(selectedRepoId === repo.id ? 0 : repo.id)}
              onSync={async () => {
                const result = await mutations.sync(repo.id);
                if (result) { setLastSync(result); refetch(); if (selectedRepoId === repo.id) refetchCommits(); }
              }}
              onDisconnect={async () => {
                await mutations.disconnect(repo.id);
                if (selectedRepoId === repo.id) setSelectedRepoId(0);
                refetch();
              }}
              syncing={mutations.loading}
            />
          ))}
        </div>
      ) : (
        <GlassPanel accent={C.cyan} delay={0.3} style={{ marginBottom: 24 }}>
          <EmptyState icon="⟁" title="No repositories connected"
            subtitle="Connect a GitHub repository to auto-import commits, map them to work items, and track lines of code produced per session."
            action={
              <button onClick={() => setShowConnect(true)} style={{
                background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
                fontFamily: F.mono, fontSize: 12, padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
              }}>CONNECT REPOSITORY</button>
            }
          />
        </GlassPanel>
      )}

      {/* ── Sync Result Flash ─────────────────────────────────────────── */}
      {lastSync && (
        <GlassPanel accent={lastSync.error ? C.coral : C.neon} delay={0} style={{ marginBottom: 16, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 18 }}>{lastSync.error ? '⚠' : '✓'}</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: lastSync.error ? C.coral : C.neon }}>
                {lastSync.error ? 'Sync Failed' : 'Sync Complete'}
              </span>
              <span style={{ fontSize: 12, color: C.muted, marginLeft: 12 }}>
                {lastSync.commitsSynced} synced · {lastSync.commitsSkipped} skipped · {lastSync.workItemsLinked} linked
              </span>
            </div>
            <button onClick={() => setLastSync(null)} style={{
              background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 14,
            }}>✕</button>
          </div>
          {lastSync.error && (
            <div style={{ fontSize: 11, color: C.coral, marginTop: 6 }}>{lastSync.error}</div>
          )}
        </GlassPanel>
      )}

      {/* ── Commit History ────────────────────────────────────────────── */}
      {selectedRepoId > 0 && (
        <>
          <SectionLabel color={C.neon}>
            Commit History — {repos?.find(r => r.id === selectedRepoId)?.fullName}
          </SectionLabel>
          <GlassPanel accent={C.neon} delay={0.2} style={{ padding: 0 }}>
            {commitsLoading ? (
              <div style={{ padding: 20 }}><LoadingShimmer height={200} /></div>
            ) : commits && commits.length > 0 ? (
              <div>
                {commits.map((commit, i) => (
                  <CommitRow key={commit.id} commit={commit} index={i} />
                ))}
              </div>
            ) : (
              <EmptyState icon="📝" title="No commits synced" subtitle="Hit the sync button on the repository to import commits." />
            )}
          </GlassPanel>
        </>
      )}

      {/* ── Connect Modal (legacy PAT flow) ────────────────────────── */}
      {showConnect && (
        <ConnectRepoModal
          projects={projects || []}
          onClose={() => setShowConnect(false)}
          onConnect={async (data) => {
            const result = await mutations.connect(data);
            if (result) { setShowConnect(false); refetch(); }
          }}
          loading={mutations.loading}
          error={mutations.error?.message}
        />
      )}

    </EnginePageShell>
  );
}

// ─── RepoCard ───────────────────────────────────────────────────────────────

function RepoCard({ repo, index, selected, onSelect, onSync, onDisconnect, syncing }: {
  repo: GitRepository; index: number; selected: boolean;
  onSelect: () => void; onSync: () => void; onDisconnect: () => void; syncing: boolean;
}) {
  const statusColor = SYNC_STATUS_COLOR[repo.syncStatus] || C.dim;

  return (
    <div
      onClick={onSelect}
      style={{
        background: selected ? C.cardHover : C.surface,
        border: `1px solid ${selected ? `${C.cyan}30` : C.faint}`,
        borderRadius: 12, padding: '14px 18px', cursor: 'pointer',
        opacity: 0, animation: `engine-feed-in 0.4s ease ${0.3 + index * 0.06}s forwards`,
        transition: 'all 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ fontSize: 20 }}>⟁</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{repo.fullName}</span>
            <StatusBadge label={repo.syncStatus} color={statusColor} glow={repo.syncStatus === 'Syncing'} />
            {repo.hasWebhook && <StatusBadge label="WEBHOOK" color={C.violet} />}
          </div>
          <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginTop: 3 }}>
            {repo.defaultBranch} · {repo.totalCommits} commits
            {repo.lastSyncDate && ` · synced ${new Date(repo.lastSyncDate).toLocaleDateString()}`}
            {repo.projectName && ` · 📂 ${repo.projectName}`}
          </div>
          {repo.syncError && (
            <div style={{ fontSize: 10, color: C.coral, marginTop: 3 }}>{repo.syncError}</div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          <button
            onClick={onSync}
            disabled={syncing || repo.syncStatus === 'Syncing'}
            style={{
              background: `${C.neon}12`, border: `1px solid ${C.neon}25`, color: C.neon,
              fontFamily: F.mono, fontSize: 9, padding: '4px 12px', borderRadius: 4, cursor: 'pointer',
              opacity: syncing ? 0.5 : 1,
            }}
          >
            {syncing ? '⏳' : '↻'} SYNC
          </button>
          <button
            onClick={onDisconnect}
            style={{
              background: 'transparent', border: `1px solid ${C.faint}`, color: C.dim,
              fontFamily: F.mono, fontSize: 9, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CommitRow ──────────────────────────────────────────────────────────────

function CommitRow({ commit, index }: { commit: GitCommit; index: number }) {
  const totalLines = commit.linesAdded + commit.linesRemoved;
  const addPct = totalLines > 0 ? (commit.linesAdded / totalLines) * 100 : 50;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 18px',
      borderBottom: `1px solid ${C.faint}`,
      opacity: 0, animation: `engine-feed-in 0.25s ease ${0.3 + index * 0.03}s forwards`,
    }}>
      {/* Hash */}
      <EngineTooltip content={<div>Full SHA: {commit.commitHash}</div>}>
        <span style={{ fontFamily: F.mono, fontSize: 12, color: C.cyan, flexShrink: 0, marginTop: 2 }}>
          {commit.shortHash}
        </span>
      </EngineTooltip>

      {/* Message + author */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, color: C.text, fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          opacity: commit.isMergeCommit ? 0.6 : 1,
        }}>
          {commit.isMergeCommit && <span style={{ color: C.dim, marginRight: 4 }}>⟁</span>}
          {commit.message.split('\n')[0]}
        </div>
        <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginTop: 2, display: 'flex', gap: 10 }}>
          <span>{commit.authorName}</span>
          <span>{new Date(commit.authorDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          {commit.branchName && <span style={{ color: C.violet }}>⌥ {commit.branchName}</span>}
          {commit.workItemName && <span style={{ color: C.amber }}>→ {commit.workItemName}</span>}
        </div>
      </div>

      {/* Diff stats */}
      <EngineTooltip content={<div>+{commit.linesAdded} −{commit.linesRemoved} across {commit.filesChanged} files</div>}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontFamily: F.mono, color: C.neon }}>+{commit.linesAdded}</span>
          <span style={{ fontSize: 11, fontFamily: F.mono, color: C.coral }}>−{commit.linesRemoved}</span>
          {/* Mini diff bar */}
          <div style={{ width: 40, height: 4, borderRadius: 2, display: 'flex', overflow: 'hidden' }}>
            <div style={{ width: `${addPct}%`, height: '100%', background: C.neon }} />
            <div style={{ width: `${100 - addPct}%`, height: '100%', background: C.coral }} />
          </div>
          <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>{commit.filesChanged}f</span>
        </div>
      </EngineTooltip>
    </div>
  );
}

// ─── ConnectRepoModal ───────────────────────────────────────────────────────

function ConnectRepoModal({ projects, onClose, onConnect, loading, error }: {
  projects: { id: number; name: string }[];
  onClose: () => void;
  onConnect: (data: { projectId: number; repositoryUrl: string; setupWebhook: boolean }) => void;
  loading: boolean;
  error?: string | null;
}) {
  const [projectId, setProjectId] = useState(0);
  const [url, setUrl] = useState('');
  const [webhook, setWebhook] = useState(false);

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
    borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
    fontSize: 13, outline: 'none',
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'engine-tooltip-in 0.2s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.panel, border: `1px solid ${C.cyan}20`, borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 500,
      }}>
        <SectionLabel color={C.cyan}>Connect Repository</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>PROJECT *</div>
            <select value={projectId} onChange={e => setProjectId(Number(e.target.value))}
              style={{ ...inputStyle, appearance: 'none' as const }}>
              <option value={0}>Select project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>REPOSITORY URL *</div>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://github.com/org/repo" style={inputStyle} autoFocus />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={webhook} onChange={e => setWebhook(e.target.checked)}
              style={{ accentColor: C.violet }} />
            <span style={{ fontSize: 12, color: C.muted }}>Generate webhook secret (manual setup in GitHub required)</span>
          </label>
          {error && <div style={{ fontSize: 12, color: C.coral, padding: '8px 12px', background: `${C.coral}10`, borderRadius: 6 }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted, fontFamily: F.mono, fontSize: 11, padding: '8px 20px', borderRadius: 6, cursor: 'pointer' }}>CANCEL</button>
          <button
            disabled={!projectId || !url.trim() || loading}
            onClick={() => onConnect({ projectId, repositoryUrl: url.trim(), setupWebhook: webhook })}
            style={{
              background: (projectId && url.trim()) ? `${C.neon}20` : `${C.dim}10`,
              border: `1px solid ${(projectId && url.trim()) ? `${C.neon}40` : C.faint}`,
              color: (projectId && url.trim()) ? C.neon : C.dim,
              fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '8px 24px',
              borderRadius: 6, cursor: (projectId && url.trim()) ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'CONNECTING...' : 'CONNECT'}</button>
        </div>
      </div>
    </div>
  );
}

